<template>
  <section v-if="available" class="ngx-filings">
    <div class="ngx-filings__head">
      <h3 class="ngx-filings__title">Company Filings</h3>
      <p class="ngx-filings__sub">Filed with the Nigerian Exchange</p>
    </div>

    <div class="ngx-filings__chips" role="group" aria-label="Filing type">
      <button
        v-for="chip in chips"
        :key="chip.key || 'all'"
        type="button"
        class="ngx-filings__chip"
        :class="{ active: chip.key === category }"
        :aria-pressed="chip.key === category"
        @click="pick(chip.key)"
      >
        {{ chip.label }}
      </button>
    </div>

    <div v-if="loading" class="ngx-filings__state"><SmallLoader /></div>
    <p v-else-if="failed" class="ngx-filings__state">{{ failed }}</p>
    <p v-else-if="!sorted.length" class="ngx-filings__state">No filings of this type yet</p>
    <ul v-else class="ngx-filings__list">
      <li v-for="filing in sorted" :key="filing.key" class="ngx-filings__row">
        <span class="ngx-filings__date">{{ filing.date }}</span>
        <div class="ngx-filings__main">
          <span class="ngx-filings__name">{{ filing.title }}</span>
          <span v-if="filing.type" class="ngx-filings__pill">{{ filing.type }}</span>
        </div>
        <a v-if="filing.url" class="ngx-filings__open" :href="filing.url" target="_blank" rel="noopener">Open PDF</a>
      </li>
    </ul>

    <p v-if="moreFailed" class="ngx-filings__note">{{ moreFailed }}</p>
    <button v-if="hasNext && !loading && !failed" type="button" class="ngx-filings__more" :disabled="loadingMore" @click="more">
      {{ loadingMore ? "Loading…" : "Show more" }}
    </button>
  </section>
</template>

<script>
import api from "@/utils/api";

const PAGE_SIZE = 10;
const UNAVAILABLE = "NGX filings are unavailable right now.";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// A filing NGX gave no date for sorts last and shows none (new Date(null) would be 1970).
const time = (at) => {
  if (!at) return 0;
  const t = new Date(at).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// "31 Jul 2026". Built by hand: en-GB now writes September as "Sept". UTC, because NGX gives
// the disclosure day as midnight UTC.
const day = (at) => {
  if (!at) return "";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const filingKey = (f) => `${f.url || ""}|${f.title || ""}|${f.disclosed_at || ""}`;

/**
 * What an NGX-listed company has filed with the exchange, from
 * GET /ngx/tokens/:id/filings: a chip per category, newest first, a page at a time.
 * Renders nothing when the first (All) call fails, 404s for a deal with no NGX listing, or
 * finds no filings at all.
 */
export default {
  name: "NgxFilings",
  props: {
    tokenId: { type: String, default: "" },
  },

  data() {
    return {
      available: false,
      categories: [],
      category: "",
      filings: [],
      page: 1,
      hasNext: false,
      loading: false,
      loadingMore: false,
      failed: "",
      moreFailed: "",
      // Bumped on every new list, so a slow answer for an old chip or token is dropped.
      seq: 0,
    };
  },

  computed: {
    chips() {
      return [{ key: "", label: "All" }, ...this.categories.filter((c) => c && c.key)];
    },

    sorted() {
      return [...this.filings]
        .sort((a, b) => time(b.disclosed_at) - time(a.disclosed_at))
        .map((f) => ({ ...f, key: filingKey(f), date: day(f.disclosed_at) }));
    },
  },

  watch: {
    tokenId: { immediate: true, handler: "load" },
  },

  methods: {
    async request(category, page) {
      const query = { page, limit: PAGE_SIZE };
      if (category) query.category = category;
      const res = await api.getNgxFilings(this.tokenId, query);
      return res?.data?.data || {};
    },

    apply(data, append) {
      const incoming = Array.isArray(data.filings) ? data.filings : [];
      if (append) {
        const seen = new Set(this.filings.map(filingKey));
        this.filings = [...this.filings, ...incoming.filter((f) => !seen.has(filingKey(f)))];
      } else {
        this.filings = incoming;
      }
      this.page = Number(data.page) || this.page;
      this.hasNext = Boolean(data.has_next);
    },

    async load(id) {
      const seq = ++this.seq;
      Object.assign(this, {
        available: false, categories: [], category: "", filings: [], page: 1, hasNext: false,
        loading: false, loadingMore: false, failed: "", moreFailed: "",
      });
      if (!id) return;
      try {
        const data = await this.request("", 1);
        if (seq !== this.seq || !(data.filings || []).length) return;
        this.categories = Array.isArray(data.categories) ? data.categories : [];
        this.apply(data, false);
        this.available = true;
      } catch (e) {
        // No NGX listing (404) or NGX is down: the card stays hidden.
      }
    },

    async pick(key) {
      if (key === this.category && !this.failed) return;
      const seq = ++this.seq;
      Object.assign(this, { category: key, filings: [], page: 1, hasNext: false, loading: true, loadingMore: false, failed: "", moreFailed: "" });
      try {
        const data = await this.request(key, 1);
        if (seq === this.seq) this.apply(data, false);
      } catch (e) {
        if (seq === this.seq) this.failed = UNAVAILABLE;
      } finally {
        if (seq === this.seq) this.loading = false;
      }
    },

    async more() {
      if (this.loadingMore || !this.hasNext) return;
      const seq = this.seq;
      this.loadingMore = true;
      this.moreFailed = "";
      try {
        const data = await this.request(this.category, this.page + 1);
        if (seq === this.seq) this.apply(data, true);
      } catch (e) {
        if (seq === this.seq) this.moreFailed = UNAVAILABLE;
      } finally {
        if (seq === this.seq) this.loadingMore = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$border-color: #e2e8f0;
$accent: #4d84ff;
$navy: #001240;

.ngx-filings {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;

  &__head { margin-bottom: 14px; }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 2px;
  }

  &__sub {
    font-size: 13px;
    color: $text-secondary;
    margin: 0;
  }

  &__chips {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    margin-bottom: 8px;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  &__chip {
    flex-shrink: 0;
    padding: 7px 14px;
    border: 1px solid $border-color;
    background: #ffffff;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;

    &:hover { color: $text-primary; border-color: #cbd5e1; }
    &.active { background: $navy; border-color: $navy; color: #ffffff; }
  }

  &__state {
    display: flex;
    justify-content: center;
    padding: 28px 0;
    margin: 0;
    font-size: 13px;
    color: $text-secondary;
    text-align: center;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid $border-color;
    &:last-child { border-bottom: 0; }

    @media (max-width: 640px) {
      grid-template-columns: minmax(0, 1fr) auto;
      row-gap: 4px;
    }
  }

  &__date {
    font-size: 12px;
    color: $text-muted;
    white-space: nowrap;
    @media (max-width: 640px) { grid-column: 1 / -1; }
  }

  &__main {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    overflow-wrap: anywhere;
  }

  &__pill {
    font-size: 11px;
    font-weight: 500;
    color: $text-secondary;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 2px 8px;
    white-space: nowrap;
  }

  &__open {
    font-size: 13px;
    font-weight: 600;
    color: $accent;
    text-decoration: none;
    white-space: nowrap;
    &:hover { text-decoration: underline; }
  }

  &__note {
    font-size: 12px;
    color: #e84343;
    margin: 8px 0 0;
  }

  &__more {
    display: block;
    width: 100%;
    margin-top: 12px;
    padding: 10px 16px;
    border: 1px solid $border-color;
    background: #ffffff;
    color: $text-primary;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) { border-color: $accent; color: $accent; }
    &:disabled { color: $text-muted; cursor: default; }
  }
}
</style>
